/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { addAlpha, styled } from '@superset-ui/core';
import { Input } from 'src/components/Input';
import type { InputProps } from 'antd/lib/input';

const SEARCH_FONT_SIZE = 14;

const SearchIcon = styled(SearchOutlined)`
  font-size: ${SEARCH_FONT_SIZE}px;
  line-height: 1;
  color: ${({ theme }) => theme.colors.grayscale.light1};
`;

export const StyledPanelSearchInput = styled(Input)`
  flex-shrink: 0;
  width: 100%;

  &&.ant-input-affix-wrapper {
    border-radius: 6px;
    border-color: ${({ theme }) => theme.colors.grayscale.light2};
    padding: ${({ theme }) =>
      `${theme.gridUnit + 2}px ${theme.gridUnit * 3}px`};
    font-size: ${SEARCH_FONT_SIZE}px;
    line-height: 1.4;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      border-color: ${({ theme }) => theme.colors.primary.light1};
    }

    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: ${({ theme }) => theme.colors.primary.base};
      box-shadow: 0 0 0 2px
        ${({ theme }) => addAlpha(theme.colors.primary.base, 0.12)};
    }

    .ant-input {
      font-size: ${SEARCH_FONT_SIZE}px;
      line-height: 1.4;
    }

    .ant-input-prefix {
      display: inline-flex;
      align-items: center;
      margin-right: ${({ theme }) => theme.gridUnit * 2}px;
      line-height: 1;

      .anticon {
        font-size: ${SEARCH_FONT_SIZE}px;
        line-height: 1;
      }

      svg {
        width: ${SEARCH_FONT_SIZE}px;
        height: ${SEARCH_FONT_SIZE}px;
      }
    }

    .ant-input-clear-icon,
    .ant-input-clear-icon .anticon {
      font-size: ${SEARCH_FONT_SIZE}px;
      line-height: 1;
    }
  }
`;

export type PanelSearchInputProps = InputProps;

const PanelSearchInput = React.forwardRef<any, PanelSearchInputProps>(
  ({ allowClear = true, prefix, ...props }, ref) => (
    <StyledPanelSearchInput
      ref={ref}
      allowClear={allowClear}
      prefix={prefix ?? <SearchIcon />}
      {...props}
    />
  ),
);

PanelSearchInput.displayName = 'PanelSearchInput';

export default PanelSearchInput;
